import { Injectable } from '@angular/core';

import { StageService } from './stage.service';
import { SizeHelperService } from './size-helper.service';
import { ConstantsService } from './constants.service';
import { ImageUtilsService } from './image-utils.service';
import { Select3dService } from './select-3d.service';
import { SetMaterialsService } from './set-materials.service';
import { ObjectCheckerService } from './object-checker.service';
import { Edges } from './railing';
import { SuperstructureBuilderService } from './superstructure-builder.service';
import { ModelLoaderService } from './model-loader.service';
import { SceneLoadingService } from './scene-loading.service';

declare const THREE: any;

interface IFloorConfig {
  points?: any[];
  height?: number;
  mainFloor?: boolean;
  customId?: number;
  parentCustomId?: number;
}

@Injectable()
export class BuildFloorService {
  private cornerPoints: any[] = [];
  private rotations: any[] = [];
  private scale: any;
  private supportPost: any;
  private floor: any; // floor container

  constructor(
    private stage: StageService,
    private sizeHelperService: SizeHelperService,
    private constants: ConstantsService,
    private imageUtils: ImageUtilsService,
    private select3dService: Select3dService,
    private materialSrvc: SetMaterialsService,
    private objectCheckerSrvc: ObjectCheckerService,
    private superstructureBuilderService: SuperstructureBuilderService,
    private modelLoaderService: ModelLoaderService,
    private sceneLoadingService: SceneLoadingService
  ) {
    this.floor = new THREE.Object3D();
    this.floor.name = 'compoundFloor';
    this.floor.position.set(0, this.constants.deckHeight, 0);
    this.stage.add(this.floor);
    
    // Уведомляем SceneLoadingService о создании пола
    setTimeout(() => {
      this.sceneLoadingService.forceCheck();
    }, 100);
  }

  private _buildFloor(config: IFloorConfig, oldFloorId?: number): any {
    let oldFloor,
      height: number,
      name: string,
      edge: any,
      isOuterTier: boolean,
      parent: boolean,
      customId: number,
      parentCustomId: number,
      oldFloorChildren: any[];

    if (oldFloorId) {
      oldFloor = this.floor.getObjectById(oldFloorId);

      this.floor.updateMatrixWorld();
      const pos = new THREE.Vector3();
      oldFloor.getWorldPosition(pos);
      height = pos.y;

      this.floor.remove(oldFloor);

      if (!config || !config.points) {
        config.points = oldFloor.userData.points;
      }

      name = oldFloor.name;
      edge = oldFloor.userData.edge;
      isOuterTier = oldFloor.userData.isOuterTier;
      parent = oldFloor.userData.parent;
      customId = oldFloor.userData.customId;
      parentCustomId = oldFloor.userData.parentCustomId;
      oldFloorChildren = oldFloor.children.filter(obj => this.objectCheckerSrvc.isAccessory(obj));
    } else {
      height = config.height || this.constants.deckHeight;
      parentCustomId = config.parentCustomId || null;
      customId = config.customId || null;
    }

    const shape = new THREE.Shape();

    for (let i = 0; i < config.points.length; i++) {
      const point = config.points[i];
      if (i === 0) {
        shape.moveTo(point.x, point.y);
      } else {
        shape.lineTo(point.x, point.y);
      }
    }

    shape.lineTo(config.points[0].x, config.points[0].y);

    const material = new THREE.MeshStandardMaterial();
    const material2 = new THREE.MeshPhongMaterial();

    const materials = [material, material2];

    const extrudeSettings = {
      steps: 2,
      amount: this.constants.deckHeight,
      bevelEnabled: false
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    const mesh = new THREE.Mesh(geometry, materials);
    mesh.rotation.x = Math.PI / 2;
    mesh.position.y = height;

    const count = config.points.length;
    const edges = [];
    for (let i = 0; i < config.points.length; i++) {
      const edge = {
        firstPoint: config.points[i],
        secondPoint: config.points[(i + 1) % count],
        length: Math.sqrt(
          Math.pow(config.points[i].x - config.points[(i + 1) % count].x, 2) +
            Math.pow(config.points[i].y - config.points[(i + 1) % count].y, 2)
        )
      };
      edges.push(edge);
    }

    mesh.userData.points = config.points;

    for (let i = 0; i < mesh.userData.points.length; i++) {
      mesh.userData.points[i].windowsDoors = config.points[i].windowsDoors;
      mesh.userData.points[i].awnings = config.points[i].awnings;
    }

    mesh.userData.parentCustomId = parentCustomId || null;
    mesh.userData.customId = customId || mesh.id; // store in userData, so it
    // can be stored again when floor is rebuilded
    mesh.name = name || 'floor';
    mesh.userData.edge = edge;
    mesh.userData.isOuterTier = isOuterTier;
    mesh.userData.parent = parent;
    mesh.userData.parentCustomId = parentCustomId;
    mesh.userData.edges = edges;
    mesh.userData.mainFloor = config.mainFloor || (oldFloor && oldFloor.userData.mainFloor);
    mesh.userData.height = height;
    mesh.userData.isOuterTier = true;
    mesh.userData.isDeckComponent = true;
    mesh.userData._edges = Edges.fromEdges(edges);
    mesh.receiveShadow = true;
    mesh.castShadow = true;

    if (oldFloorChildren && oldFloorChildren.length !== 0) {
      mesh.add.apply(mesh, oldFloorChildren);
    }

    this.stage.add(mesh);
    THREE.SceneUtils.attach(mesh, this.stage.getScene(), this.floor);

    this.select3dService.setSelectedTier(mesh);
    if (!this.imageUtils.isEnabled()) {
      this.imageUtils.enable();
    }
    this.imageUtils.drawTextSprites();
    oldFloor && this.changeParentIdToFloors(oldFloor.id, mesh.id);

    let j = 0;
    mesh.userData.points.forEach(point => {
      if (
        point.width &&
        point.edge &&
        point.parent &&
        (point.superstructureType === 'tier1StepUp' || point.superstructureType === 'tier1StepDown')
      ) {
        const A = new THREE.Vector3(point.edge.firstPoint.x, 0, point.edge.firstPoint.y);
        const B = new THREE.Vector3(point.edge.secondPoint.x, 0, point.edge.secondPoint.y);

        const AB = new THREE.Vector3(B.x - A.x, 0, B.z - A.z);
        let AB2 = AB.clone();
        AB2.normalize();

        let vect = new THREE.Vector3(AB.z, 0, -AB.x);
        let norm = vect.clone().normalize();
        var norm1 = new THREE.Vector3(0, 0, 1);
        let angle = norm1.angleTo(vect);

        let parentTier = this.stage
          .getCompoundFloor()
          .children.find(tier => tier.userData.customId === parentCustomId);
        let parentFirstPointIndex = parentTier.userData.points.findIndex(
          ppoint => ppoint.childCustomId === mesh.userData.customId
        );
        let parentFirstPoint = parentTier.userData.points[parentFirstPointIndex];
        let parentSecondPointIndex =
          parentFirstPointIndex + 1 === parentTier.userData.points.length
            ? 0
            : parentFirstPointIndex + 1;
        let parentSecondPoint = parentTier.userData.points[parentSecondPointIndex];

        let C = new THREE.Vector3(parentFirstPoint.x, 0, parentFirstPoint.y);
        let D = new THREE.Vector3(parentSecondPoint.x, 0, parentSecondPoint.y);
        let CD = new THREE.Vector3(D.x - C.x, 0, D.z - C.z);
        let DB = new THREE.Vector3(B.x - D.x, 0, B.z - D.z);
        let DC = new THREE.Vector3(C.x - D.x, 0, C.z - D.z);
        let CA = new THREE.Vector3(A.x - C.x, 0, A.z - C.z);
        let DA = new THREE.Vector3(A.x - D.x, 0, A.z - D.z);

        let projectionLength = (DB.x * DC.x + DB.z * DC.z) / CD.length();
        let projectionLength2 = (CD.x * CA.x + CD.z * CA.z) / CD.length();
        let minLength = Math.min(projectionLength, projectionLength2, AB.length(), CD.length());

        let projectParentSPOnThisFP = (DA.x * DC.x + DA.z * DC.z) / CD.length();
        let positionPoint = point.firstPoint;
        if (projectParentSPOnThisFP < 0) {
          positionPoint = parentSecondPoint;
        }
        mesh.userData.points[j].width = AB.length();
        mesh.userData.points[j].distance = AB.length() / 2;

        if (minLength - point.distance < point.width / 2) {
          mesh.userData.points[j].distance = minLength - point.width / 2;
        }
        if (point.distance < point.width / 2) {
          mesh.userData.points[j].distance = point.width / 2;
        }
        if (minLength < point.width) {
          mesh.userData.points[j].width = minLength;
          mesh.userData.points[j].distance = point.width / 2;
        }

        mesh.userData.points[j].terminationPosts = null;
      }
      j++;
    });

    return mesh;
  }

  buildFloor(config: IFloorConfig, oldFloorId?: number): any {
    const floor = this._buildFloor(config, oldFloorId);

    this.addSupport(floor);

    this.materialSrvc.setMaterial(this.materialSrvc.getCurrentFloorTexture());

    this.superstructureBuilderService.buildSuperstructuresForTier(floor);

    return floor;
  }

  private async addSupport(floor) {
    const obj = await this.modelLoaderService.loadLocalModel('Deck-Support');
    this.setPositionsForSupportPosts(floor);
    const texture = new THREE.TextureLoader().load('../../assets/images/deck_fascia.png');
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    const support = obj.children[0];
    support.material = new THREE.MeshPhongMaterial({
      map: texture,
      specular: 0x111111,
      shininess: 30
    });
    support.castShadow = true;
    this.supportPost = obj.clone();
    const posts = this.setPostsPositions();
    this.setScaleForSupportPosts(posts, floor.userData.height);
    this.stage.addMany(posts);
    for (let i = 0; i < posts.length; i++) {
      floor.updateMatrixWorld(true);
      posts[i].updateMatrixWorld(true);
      THREE.SceneUtils.attach(posts[i], this.stage.getScene(), floor);
    }
  }

  async buildFloorAsync(config: IFloorConfig, oldFloorId?: number): Promise<any> {
    const floor = this._buildFloor(config, oldFloorId);

    await this.addSupport(floor);

    await this.materialSrvc.setMaterial(this.materialSrvc.getCurrentFloorTexture());

    await this.superstructureBuilderService.buildSuperstructuresForTier(floor);

    return floor;
  }

  // need to check if does anything and if needed at all
  changeParentIdToFloors(oldFloor, newFloor) {
    for (let i = 0; i < this.floor.children.length; i++) {
      if (this.floor.children[i].userData.parent == oldFloor) {
        this.floor.children[i].userData.parent = newFloor;
      }
    }
  }

  removeFloor() {
    while (this.floor.children.length) {
      this.floor.remove(this.floor.children[0]);
    }
  }

  // refactor
  // this and next method naming confusing
  setPostsPositions(): Array<any> {
    let supPosts = [];
    for (let i = 0; i < this.cornerPoints.length; i++) {
      let post = this.supportPost.clone();
      post.name = 'supportPost';
      post.position.set(this.cornerPoints[i].x, this.cornerPoints[i].y, this.cornerPoints[i].z);
      post.rotation.y = this.rotations[i];
      post.scale.y = this.scale;
      post.visible = true;
      supPosts.push(post);
    }
    return supPosts;
  }

  setPositionsForSupportPosts(floor) {
    this.cornerPoints = [];
    this.rotations = [];

    let dHeight = floor.userData.height - this.sizeHelperService.INCHToCM(8);
    let points = floor.userData.points;

    for (let i = 0; i < points.length; i++) {
      let point = new THREE.Vector3(points[i].x, dHeight / 2, points[i].y);

      let A = points[i],
        B = points[(i + 1) % points.length],
        C = points[i === 0 ? points.length - 1 : i - 1];
      let firstWallVector = new THREE.Vector3(B.x - A.x, 0, B.y - A.y);
      let secondWallVector = new THREE.Vector3(C.x - A.x, 0, C.y - A.y);

      let n =
        this.sizeHelperService.INCHToCM(2.5) +
        +this.constants.supportPostSize.width * Math.cos(Math.PI / 4);

      firstWallVector.setLength(n);
      secondWallVector.setLength(n);
      let rot = secondWallVector.clone();
      rot.normalize();
      let med = new THREE.Vector3(
        firstWallVector.x + secondWallVector.x,
        0,
        firstWallVector.z + secondWallVector.z
      );
      let norm = new THREE.Vector3(1, 0, 0);
      let angle = Math.acos(rot.x); // secondWallVector.angleTo(norm);// + Math.PI / 4;
      // console.log(angle);
      if (firstWallVector.x * secondWallVector.z - firstWallVector.z * secondWallVector.x < 0) {
        // && norm.x * secondWallVector.z - norm.z * secondWallVector.x > 0) {
        angle += Math.PI / 2;
        // console.log('changed: ', angle);
      }
      // this.rotations.push(angle);
      let orient = firstWallVector.x * secondWallVector.z - firstWallVector.z * secondWallVector.x;
      // is inside angle?
      if (orient < 0) {
        firstWallVector.negate();
        secondWallVector.negate();
      }
      this.rotations.push(angle);

      firstWallVector.setLength(n);
      secondWallVector.setLength(n);

      point.add(firstWallVector);
      point.add(secondWallVector);
      this.cornerPoints.push(point);
    }
  }

  setScaleForSupportPosts(supPosts, deckHeight) {
    let scale =
      (deckHeight - this.sizeHelperService.INCHToCM(8) + 0.01) /
      +this.constants.supportPostSize.height;

    this.scale = scale;
    if (supPosts[0]) {
      for (let i = 0; i < supPosts.length; i++) {
        supPosts[i].scale.y = this.scale;
        supPosts[i].position.y = (deckHeight - this.sizeHelperService.INCHToCM(8)) / 2;
      }
    }
  }

  rescaleSupportPosts() {
    for (let i = 0; i < this.floor.children.length; i++) {
      const floor = this.floor.children[i];
      this.floor.updateMatrixWorld(true);
      // floor.updateMatrixWorld();
      let pos = new THREE.Vector3();
      floor.getWorldPosition(pos);
      let deckHeight = pos.y - this.sizeHelperService.INCHToCM(8);

      let scale = (deckHeight + 0.01) / +this.constants.supportPostSize.height;

      // add sup posts to separate array,
      // because detach will remove from children
      // and change indexes
      let posts = [];
      for (let t = 0; t < floor.children.length; t++) {
        if (floor.children[t].name === 'supportPost') posts.push(floor.children[t]);
      }
      for (let j = 0; j < posts.length; j++) {
        const supPost = posts[j];
        THREE.SceneUtils.detach(supPost, floor, this.stage.getScene());
        supPost.scale.y = scale;
        supPost.position.y = deckHeight / 2;
        // floor.updateMatrixWorld();
        supPost.updateMatrixWorld(true);
        THREE.SceneUtils.attach(supPost, this.stage.getScene(), floor);
      }
    }
  }

  setCompundFloorPosY(y: number) {
    this.floor.position.set(0, y, 0);
    this.floor.updateMatrixWorld(true);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/build-floor.service.ts